/**
 * LookupDataGrid.tsx
 * Inline editable grid for lookup code/name pairs.
 * Redesigned — uses shared GenWizard light theme.
 */

import React, { Component, ChangeEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';

import { T, injectTheme } from './theme/theme';

// ─── Types ────────────────────────────────────────────────────────────────────
interface LookupData {
  id:   number;
  Code: string;
  Name: string;
}

interface LookupDataGridProps {
  data: LookupData[];
  handleLookUpValueChanges: (data: LookupData[]) => void;
}

interface LookupDataGridState {
  data: LookupData[];
}

// ─── Component ────────────────────────────────────────────────────────────────
class LookupDataGrid extends Component<LookupDataGridProps, LookupDataGridState> {
  constructor(props: LookupDataGridProps) {
    super(props);
    this.state = { data: this.normalise(props.data) };
  }

  componentDidMount() {
    injectTheme();
  }

  componentDidUpdate(prevProps: LookupDataGridProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({ data: this.normalise(this.props.data) });
    }
  }

  // Sort + ensure every row has an id
  normalise(raw: LookupData[]): LookupData[] {
    const data = [...(raw || [])].sort((a, b) => (a.Name || '').localeCompare(b.Name || ''));
    let ix = 0;
    for (const d of data) { if (!d.id) d.id = ix++; }
    return data;
  }

  handleInputChange = (id: number, field: keyof LookupData, value: string) => {
    const updated = this.state.data.map(row => row.id === id ? { ...row, [field]: value } : row);
    this.setState({ data: updated });
    this.props.handleLookUpValueChanges(updated);
  };

  addRow = () => {
    const newRow: LookupData = { id: Date.now(), Code: '', Name: '' };
    this.setState(prev => ({ data: [...prev.data, newRow] }));
  };

  deleteRow = (id: number) => {
    const updated = this.state.data.filter(row => row.id !== id);
    this.setState({ data: updated });
    this.props.handleLookUpValueChanges(updated);
  };

  render() {
    const { data } = this.state;

    return (
      <div className="gw-card">

        {/* ── Header ── */}
        <div className="gw-card-hdr">
          <div className="gw-card-title">
            Lookup Values
            <span className="gw-badge gw-badge-muted" style={{ marginLeft: 8 }}>
              {data.length}
            </span>
          </div>
          <button className="gw-btn gw-btn-outline gw-btn-sm" onClick={this.addRow}>
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} />
            Add Row
          </button>
        </div>

        {/* ── Table ── */}
        <div style={{ maxHeight: 380, overflowY: 'auto' }} className="gw-scroll">
          <table className="gw-table" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th style={{ width: '38%' }}>Code</th>
                <th>Name / Description</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (
                <tr>
                  <td colSpan={3}>
                    <div className="gw-empty" style={{ padding: '20px 0' }}>
                      <div className="gw-empty-label">No lookup values — click Add Row to begin.</div>
                    </div>
                  </td>
                </tr>
              )}
              {data.map(row => (
                <tr key={row.id}>
                  <td style={{ padding: '6px 10px' }}>
                    <input
                      className="gw-input"
                      style={{ padding: '5px 9px', fontSize: 12 }}
                      type="text"
                      value={row.Code}
                      placeholder="CODE"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        this.handleInputChange(row.id, 'Code', e.target.value)
                      }
                    />
                  </td>
                  <td style={{ padding: '6px 10px' }}>
                    <input
                      className="gw-input"
                      style={{ padding: '5px 9px', fontSize: 12 }}
                      type="text"
                      value={row.Name}
                      placeholder="Description"
                      onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        this.handleInputChange(row.id, 'Name', e.target.value)
                      }
                    />
                  </td>
                  <td style={{ padding: '6px 10px', textAlign: 'center' }}>
                    <button
                      className="gw-btn gw-btn-danger gw-btn-icon gw-btn-sm"
                      title="Delete row"
                      onClick={() => this.deleteRow(row.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} style={{ fontSize: 10 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}

export default LookupDataGrid;